import * as React from 'react'
import {
  BlockRelationship,
  RootBlock,
  getRepoSpaceName,
  isBRInRoot,
} from '@basehub/data/replicache/index'
import { useQuery } from '@tanstack/react-query'
import { getCommitBlockTreeManagerQueryFn } from './query'
import { useSelectedTeamAndRepo } from '../use-selected-team-and-repo'
import { useRepoReplicache } from '../use-repo-replicache'
import { sleep } from '~/lib/sleep'
import { useDebouncedCallback } from '~/hooks/use-debounced-callback'

export const useCommitBlockTreeManager = ({
  commitId,
  rootBlockId,
  keepPreviousData,
  extraDeps,
}: {
  commitId: string | undefined
  rootBlockId: string | undefined
  keepPreviousData?: boolean
  caller?: string
  extraDeps?: Array<unknown>
}) => {
  const { data } = useSelectedTeamAndRepo()
  const { data: repoReplicacheData } = useRepoReplicache()
  const teamId = data?.team?.id
  const repoId = data?.repo?.id

  const queryKey = React.useMemo(
    () =>
      getCommitBlockTreeManagerQueryKey({
        commitId,
        teamId,
        repoId,
        extraDeps,
      }),
    [commitId, repoId, teamId, extraDeps]
  )

  const queryResult = useQuery(
    queryKey,
    async () => {
      if (teamId && repoId && commitId && rootBlockId) {
        const result = await getCommitBlockTreeManagerQueryFn({
          replicache: repoReplicacheData?.replicache,
          spaceId: getRepoSpaceName(teamId, repoId),
          commitId,
          rootBlockId,
        })

        return result
      } else {
        return null
      }
    },
    {
      enabled: Boolean(teamId && repoId && commitId && rootBlockId),
      staleTime: Infinity, // immutable
      refetchOnWindowFocus: false,
      networkMode: 'always',
      keepPreviousData,
    }
  )

  const rootBlock = queryResult.data?.root.value
    ? (queryResult.data.root.value as RootBlock)
    : null
  const refetch = queryResult.refetch
  const refetchDebounced = useDebouncedCallback(refetch, [refetch], 500)

  React.useEffect(() => {
    if (!repoReplicacheData?.replicache || !rootBlock) return
    const unsub = repoReplicacheData.replicache.experimentalWatch(
      async (diff) => {
        await sleep(1) // nasty, but wait for the main experimentalWatch in provider to finish

        let shouldRefetch = false

        for (const diffOp of diff) {
          if (diffOp.op === 'del') continue
          // updates or additions might be relevant to this tree
          if (isBRInRoot(diffOp.newValue as BlockRelationship, rootBlock)) {
            shouldRefetch = true
          }
        }

        if (shouldRefetch) {
          refetchDebounced()
        }
      },
      {
        prefix: 'br/',
        initialValuesInFirstDiff: false,
      }
    )

    return unsub
  }, [repoReplicacheData?.replicache, rootBlock, refetchDebounced])

  if (queryResult.error) {
    console.error(queryResult.error)
  }

  return queryResult
}

export const getCommitBlockTreeManagerQueryKey = ({
  commitId,
  teamId,
  repoId,
  extraDeps = [],
}: {
  commitId: string | undefined
  teamId: string | undefined
  repoId: string | undefined
  extraDeps?: Array<unknown>
}) => {
  return ['commit-tree', commitId, teamId, repoId, ...extraDeps] as const
}
